import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import Link from 'next/link';
import styles from 'sections/Resources/BillOfLading/components/SectionSteps/SectionSteps.module.scss';

const cn = classNames.bind(styles);

const SectionSteps = (): ReactElement => {
  return (
    <section className={cn('steps')}>
      <div className={cn('container')}>

        <div className={cn('steps__heading-box')}>
          <h2 className={cn('steps__heading')}>Practical Steps for Efficient Bill of Lading Management</h2>
          <p className={cn('steps__heading-description')}>
            To manage Bills of Lading effectively, it is important to follow best practices that ensure
            accuracy, compliance, and efficiency. Here are some practical steps to help you achieve this:
          </p>
        </div>

        <ul className={cn('steps__benefits')}>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Automate Documentation Processes</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Use our digital tool to automate
              the creation and management of Bills of Lading. Automation helps reduce the risk of errors,
              speeds up the process, and ensures that all documentation is compliant with legal requirements.
              By using automation, you can also free up time and resources that can be used for other important tasks.
            </p>
          </li>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Ensure Compliance</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Stay up-to-date with the latest regulations and ensure that your Bills of Lading comply with all
              relevant laws. This includes understanding the specific requirements for different types of
              BoLs and ensuring that they are accurately completed and processed. Compliance is crucial to
              avoid legal disputes, fines, and shipment delays.
            </p>
          </li>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Maintain Accurate Records</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Keep detailed records of all Bills of Lading and related documentation. Accurate record-
              keeping helps you track shipments, resolve disputes, and demonstrate compliance with legal
              requirements. It also provides a valuable reference for future shipments and helps ensure that
              all aspects of the logistics process are managed effectively.
            </p>
          </li>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Train Your Team</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Provide training for your team on the importance of Bills of Lading, how to complete them
              accurately, and how to use <Link href='/digital-tools'>logistics tools</Link> for BoL management. Professional development in
              the form of online courses, webinars or in person sessions helps ensure that your team
              understands the legal implications of BoLs and is equipped to manage them effectively,
              reducing the risk of errors and improving business efficiency.
            </p>
          </li>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Implement Robust Tracking Systems</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Use <Link href='/digital-tools'>multimodal tracking systems</Link> like CARGOES Flow, to monitor the status of your shipments
              and ensure that you are always informed about their progress. Robust tracking systems help
              you anticipate potential issues, manage delays, and ensure that your cargo is delivered on time
              and in good condition.
            </p>
          </li>
          <li className={cn('steps__benefit')}>
            <h3 className={cn('steps__benefit-heading')}>Streamline Communication</h3>
            <p className={cn('steps__benefit-paragraph')}>
              Maintain clear and effective communication with all parties involved in the shipping process,
              including shippers, carriers, and consignees. Clear communication helps ensure that everyone
              understands their responsibilities and that any issues are resolved quickly and effectively. It
              also helps reduce the risk of misunderstandings and disputes.
            </p>
          </li>
        </ul>

        <div className={cn('steps__summary')}>
          <p className={cn('steps__summary-paragraph')}>
            Managing Bills of Lading is a critical aspect of shipping and logistics that requires careful attention
            and expertise. By using our tools you can streamline the process, reduce
            errors, and ensure compliance. These tools provide a comprehensive solution for managing Bills of
            Lading, from creation to tracking, helping you improve efficiency and reduce administrative burdens.
          </p>
          <p className={cn('steps__summary-paragraph')}>
            For more information on how the Digital Freight Alliance can help you manage your shipping process and
            improve your logistics operations, contact us today. We offer a range of services and tools designed to
            meet the needs of freight forwarders and NVOCC managers, helping you navigate the complexities of the
            logistics industry and achieve your business goals.
          </p>
          <p className={cn('steps__summary-paragraph')}>
            Legal Framework and Compliance The BoL is governed by a range of international conventions, such as the
            Hague-Visby Rules, the Hamburg Rules, and the Rotterdam Rules, which set out the rights and obligations
            of carriers and shippers. These conventions provide a standardized legal framework that facilitates
            international trade by ensuring that there are clear rules governing the transport of goods. In addition
            to these international laws, Bills of Lading are also subject to national regulations, which can vary
            significantly from country to country. Compliance with these regulations is crucial to avoid legal
            disputes, fines, and delays.
          </p>
          <p className={cn('steps__summary-paragraph')}>
            Consequences of Errors and Non-Compliance Errors in a Bill of Lading, such as incorrect descriptions of
            goods, missing information, or misrepresentations, can lead to significant issues, including delays in
            shipment, additional costs, and legal disputes. For example, if the goods are misdescribed on the BoL,
            customs authorities may detain the shipment, resulting in delays and additional inspection fees.
            Moreover, non-compliance with legal requirements can result in fines, penalties, and potential
            litigation. Ensuring accuracy and compliance in the preparation of Bills of Lading is therefore
            essential to avoid these risks and maintain smooth logistics operations.
          </p>
        </div>
      </div>
    </section>
  );
};

export default SectionSteps;